import PropertyCard from "@/components/frontend/marketsuite/property/card";
import { Pagination, type PaginationMeta } from "@/components/frontend/ui/pagination";
import type { ButtonVariant } from "@/components/frontend/ui/button";
import type { CardVariant } from "@/components/frontend/ui/card";
import { MapPinHouse } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import type { Property } from "@/lib/types/marketsuite/datatypes/property";
import type { MsRoutes } from "@/lib/types/marketsuite/property-route";
import { PropertyOverviewSort } from "./sort";
import { FormVariant } from "@/lib/types/form/form";

const columnMap = {
	"2": "grid-cols-2",
	"3": "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
	"4": "grid-cols-1 sm-grid-cols-2 md-grid-cols-3 lg:grid-cols-4",
};

export interface PropertyOverviewResultsData {
	data: Partial<Property>[];
	meta: PaginationMeta;
}

export interface PropertyOverviewResultsProps {
	basePath: string;
	columns?: keyof typeof columnMap | null;
	data?: PropertyOverviewResultsData;
	filterQuery?: string;
	itemListId: string;
	itemListName: string;
	msRoutes?: MsRoutes;
	paginationButtonVariant?: ButtonVariant;
	propertyCardVariant?: CardVariant;
	sortFormVariant?: FormVariant;
	sort?: string;
	pending?: boolean;
	onPageChange?: (page: number) => void;
	onSortChange?: (sort: string) => void;
}

export const sortOptionValues = ["-created_at", "created_at", "price", "-price", "living_area", "-living_area", "available_at", "-available_at"];

export function PropertyOverviewResults({
	basePath,
	columns,
	data,
	filterQuery,
	itemListId,
	itemListName,
	msRoutes = {},
	paginationButtonVariant,
	propertyCardVariant,
	pending = false,
	sortFormVariant = "default",
	sort,
	onPageChange,
	onSortChange,
}: PropertyOverviewResultsProps) {
	const t = useTranslations("ms");
	const sortOptions = [
		{ label: t("property-overview.sort-placeholder"), value: "" },
		...sortOptionValues.map((value) => ({
			label: t(`property-overview.sort-options.${value}`),
			value,
		})),
	];

	if (!data?.data.length) {
		return (
			<div className="flex flex-col items-center w-full justify-start gap-2 py-16">
				<MapPinHouse className="h-12 w-12" />

				<div className="text-center">
					<p className="h3">{t("property-overview.no-results-title")}</p>
					<p className="text-sm">{t("property-overview.no-results-description")}</p>
				</div>
			</div>
		);
	}

	return (
		<div className={cn("space-y-4 w-full", pending && "opacity-60 transition-opacity")}>
			<div className="flex justify-between items-center">
				<p className="text-sm sm:text-lg">{t("property-overview.results-summary", { total: data.meta.total })}</p>

				<div className={"flex items-center gap-2 ms-form-" + sortFormVariant}>
					<span className="hidden sm:block">{t("property-overview.sort-by")}</span>

					<PropertyOverviewSort
						basePath={basePath}
						filterQuery={filterQuery}
						formVariant={sortFormVariant}
						options={sortOptions}
						sort={sort}
						onSortChange={onSortChange}
					/>
				</div>
			</div>

			<ol className={cn("grid gap-x-4 gap-y-6", columnMap[columns ?? "3"])}>
				{data.data.map((property, index) => (
					<li key={property.id}>
						<PropertyCard
							urlBase={msRoutes[property.listing_type!]?.detailUrlBase ?? ""}
							variant={propertyCardVariant}
							property={property}
							itemListId={itemListId}
							itemListName={itemListName}
							itemIndex={index}
						/>
					</li>
				))}
			</ol>

			{data.meta.last_page > 1 && (
				<Pagination data={data} variant={paginationButtonVariant} pagePath={{ basePath, query: filterQuery }} onPageChange={onPageChange} />
			)}
		</div>
	);
}
